import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';

import type { RoomWithKey } from './types';

interface Props {
  attribute: 'amenities' | 'description';
  onCopy: (room: RoomWithKey) => void;
  rooms: Array<RoomWithKey>;
  roomKey: string;
}

export default function CopyRoomAttributeButton({ attribute, onCopy, rooms, roomKey }: Props) {
  const [open, setOpen] = useState(false);
  const [sourceRoomKey, setSourceRoomKey] = useState('');
  const sourceRooms = rooms.filter((room) => room.roomKey !== roomKey);

  const close = () => {
    setOpen(false);
    setSourceRoomKey('');
  };

  const handleCopy = () => {
    const sourceRoom = sourceRooms.find((room) => room.roomKey === sourceRoomKey);

    if (!sourceRoom) {
      return;
    }

    onCopy(sourceRoom);
    close();
  };

  if (sourceRooms.length === 0) {
    return null;
  }

  const label = attribute;

  return (
    <>
      <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => setOpen(true)}>
        Copy from another room
      </Button>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Copy {label} from another room</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will copy over existing details but will not automatically save the room type. You will have the
            opportunity to review and make changes.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Copy from"
            value={sourceRoomKey}
            onChange={(event) => setSourceRoomKey(event.target.value)}
            sx={{ mt: 1 }}
          >
            {sourceRooms.map((room) => (
              <MenuItem key={room.roomKey} value={room.roomKey}>
                {room.name || 'Unnamed room'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCopy} disabled={!sourceRoomKey}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
